"use client";

const ATTRIBUTE_IMAGES: Record<string, string> = {
  strike: "/images/attributes/strike.webp",
  special: "/images/attributes/special.webp",
  slash: "/images/attributes/slash.webp",
  ranged: "/images/attributes/ranged.webp",
  wisdom: "/images/attributes/wisdom.webp",
};

const COMBO_IMAGES: Record<string, string> = {
  "slash_special": "/images/attributes/slash_special.webp",
  "slash_strike": "/images/attributes/slash_strike.webp",
  "strike_special": "/images/attributes/strike_special.webp",
  "strike_ranged": "/images/attributes/strike_ranged.webp",
};

export function attributeImageUrl(attribute: string): string | null {
  const key = attribute.trim().toLowerCase();
  return ATTRIBUTE_IMAGES[key] ?? null;
}

/**
 * For multi-attribute cards, try to find a combo image.
 * Returns the combo image URL if available, or null.
 */
export function comboAttributeImageUrl(attributes: string[]): string | null {
  const keys = attributes
    .map((a) => a.trim().toLowerCase())
    .filter(Boolean)
    .sort();
  if (keys.length < 2) return null;

  const combo = keys.join("_");
  if (COMBO_IMAGES[combo]) return COMBO_IMAGES[combo];

  const reversed = keys.reverse().join("_");
  if (COMBO_IMAGES[reversed]) return COMBO_IMAGES[reversed];

  return null;
}

export function AttributeBadge({
  attribute,
  size = "sm",
  showLabel = true,
}: {
  attribute: string;
  size?: "xs" | "sm" | "md";
  showLabel?: boolean;
}) {
  const name = attribute.trim();
  if (!name) return null;

  const imgUrl = attributeImageUrl(name);
  const sizeClass = size === "xs" ? "h-3.5 w-3.5" : size === "sm" ? "h-4 w-4" : "h-5 w-5";
  const textClass = size === "xs" ? "text-2xs" : size === "sm" ? "text-xs" : "text-sm";

  return (
    <span className={`inline-flex items-center gap-1 ${textClass}`}>
      {imgUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imgUrl}
          alt={name}
          className={`${sizeClass} object-contain`}
        />
      )}
      {showLabel && <span>{name}</span>}
    </span>
  );
}

export function AttributeBadges({
  attributes,
  size = "sm",
  showLabel = true,
}: {
  attributes: string[];
  size?: "xs" | "sm" | "md";
  showLabel?: boolean;
}) {
  const filtered = attributes.filter((a) => a.trim());
  if (!filtered.length) return null;

  const comboUrl = comboAttributeImageUrl(filtered);
  const sizeClass = size === "xs" ? "h-3.5 w-3.5" : size === "sm" ? "h-4 w-4" : "h-5 w-5";

  return (
    <span className="inline-flex items-center gap-1.5">
      {comboUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={comboUrl}
          alt={filtered.join("/")}
          className={`${sizeClass} object-contain`}
        />
      )}
      {filtered.map((attr) => (
        <AttributeBadge key={attr} attribute={attr} size={size} showLabel={showLabel} />
      ))}
    </span>
  );
}
